import React, { useEffect, useState } from "react";
import Swal from "sweetalert2";
import { useQuotation } from "../useHook";
import { usePermission, SystemPermissions } from "../../../Utils/permissionAccess";

const BILL_FROM = {
  billFromAddress: "12 B/1, Johar town, Lahore",
  billFromPhone: "+92 315 5111119",
  billFromEmail: "info@gharplans.pk",
};

function generateInvoiceNo() {
  const letters = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
  const digits = "0123456789";
  const pick = (str, n) =>
    Array.from({ length: n }, () => str[Math.floor(Math.random() * str.length)]).join("");
  const letterCount = 2 + Math.floor(Math.random() * 2); // 2 or 3 letters
  const digitCount = 4 - letterCount; // 2 or 1 digit
  return `GHAR-PLANS-${pick(letters, letterCount)}${pick(digits, digitCount)}`;
}

/** Pakistan mobile: 03XXXXXXXXX (11 digits) or +92 3XXXXXXXXX */
function isValidPakistanPhone(value) {
  if (!value || typeof value !== "string") return false;
  const digits = value.replace(/\D/g, "");
  return /^03[0-9]{9}$/.test(digits) || /^923[0-9]{9}$/.test(digits);
}

function QuotationPage() {
  const { hasPermission } = usePermission();
  const canGenerate = hasPermission(SystemPermissions.GenerateQuotation.GENERATE);

  const currentDate = new Date().toISOString().split("T")[0];
  const { getProducts, generatePdf, getVarinatByProductId, getColorsByVariantId } = useQuotation();
  const [productList, setProductList] = useState([]);
  const [pdfFileLink, setPdfFileLink] = useState(null);
  const [quotationData, setQuotationData] = useState({
    quotation: { invoiceNo: generateInvoiceNo(), date: currentDate },
    billTo: { billToName: "", billToPhone: "", billToAddress: "", date: currentDate },
    products: [
      {
        productId: "",
        variantId: "",
        variantColorId: "",
        variants: [],
        colors: [],
        quantity: "",
        weight: "",
      },
    ],
  });
  const [isLoading, setIsLoading] = useState(false);
  const [phoneError, setPhoneError] = useState("");

  useEffect(() => {
    getProducts(setProductList);
  }, []);
  const handleChange = (section, field, value, index = null) => {
    if (index !== null && field === "productId") {
      setQuotationData((prev) => {
        const updatedProducts = [...prev.products];
        updatedProducts[index] = {
          ...updatedProducts[index],
          productId: value,
          variantId: "",
          variantColorId: "",
          variants: [],
          colors: [],
          weight: "",
        };
        return { ...prev, products: updatedProducts };
      });
      if (value) {
        getVarinatByProductId(value).then((data) => {
          const variants = data?.variants || [];
          setQuotationData((prev) => {
            const updatedProducts = [...prev.products];
            updatedProducts[index] = {
              ...updatedProducts[index],
              productId: value,
              variants,
            };
            return { ...prev, products: updatedProducts };
          });
        });
      }
      return;
    }
    if (index !== null && field === "variantId") {
      setQuotationData((prev) => {
        const updatedProducts = [...prev.products];
        const selectedVariant = (updatedProducts[index].variants || []).find(
          (v) => String(v.id) === String(value)
        );
        updatedProducts[index] = {
          ...updatedProducts[index],
          variantId: value,
          variantColorId: "",
          colors: [],
          weight: selectedVariant?.weight ?? "",
        };
        return { ...prev, products: updatedProducts };
      });
      if (value) {
        getColorsByVariantId(value).then((data) => {
          const colors = data?.colors || [];
          setQuotationData((prev) => {
            const updatedProducts = [...prev.products];
            updatedProducts[index] = {
              ...updatedProducts[index],
              colors,
            };
            return { ...prev, products: updatedProducts };
          });
        });
      }
      return;
    }
    setQuotationData((prev) => {
      if (index !== null) {
        const updatedProducts = [...prev.products];
        updatedProducts[index] = { ...updatedProducts[index], [field]: value };
        return { ...prev, products: updatedProducts };
      }
      return { ...prev, [section]: { ...prev[section], [field]: value } };
    });
    if (field === "billToPhone") {
      const trimmed = (value || "").trim();
      setPhoneError(trimmed && !isValidPakistanPhone(trimmed) ? "Enter a valid Pakistan mobile (e.g. 03001234567 or +92 300 1234567)" : "");
    }
  };

  const handleAddProduct = () => {
    setQuotationData((prev) => ({
      ...prev,
      products: [
        ...prev.products,
        {
          productId: "",
          variantId: "",
          variantColorId: "",
          variants: [],
          colors: [],
          quantity: "",
          weight: "",
        },
      ],
    }));
  };

  const handleRemoveProduct = (index) => {
    setQuotationData((prev) => {
      const updatedProducts = prev.products.filter((_, i) => i !== index);
      return { ...prev, products: updatedProducts };
    });
  };

  const buildPayload = () => {
    const invoiceNo = quotationData.quotation?.invoiceNo || generateInvoiceNo();
    const date = quotationData.billTo?.date ?? quotationData.quotation?.date ?? currentDate;

    const payload = {
      quotation: { invoiceNo, date },
      billFrom: { ...BILL_FROM },
      billTo: { ...quotationData.billTo },
      products: quotationData.products.map((p) => ({
        productId: Number(p.productId),
        variantId: Number(p.variantId),
        variantColorId: Number(p.variantColorId),
        quantity: Number(p.quantity) || 0,
        weight: p.weight != null && p.weight !== "" ? String(p.weight) : undefined,
      })),
    };
    return payload;
  };

  const handleSubmit = async () => {
    const phone = (quotationData.billTo?.billToPhone || "").trim();
    if (phone && !isValidPakistanPhone(phone)) {
      setPhoneError("Enter a valid Pakistan mobile (e.g. 03001234567 or +92 300 1234567)");
      Swal.fire({
        title: "Validation Error",
        text: "Phone must be a valid Pakistan mobile number (03XXXXXXXXX or +92 3XX XXXXXXX).",
        icon: "warning",
      });
      return;
    }
    setPhoneError("");
    const invalid = quotationData.products.some(
      (p) => !p.productId || !p.variantId || !p.variantColorId || !p.quantity
    );
    if (invalid) {
      Swal.fire({
        title: "Validation Error",
        text: "Each row must have Product, Variant, Color, and Quantity.",
        icon: "warning",
      });
      return;
    }
    try {
      setIsLoading(true);
      const payload = buildPayload();
      const fileUrl = await generatePdf(payload, setPdfFileLink);
      if (!fileUrl) throw new Error("Failed to generate PDF");
    } catch (error) {
      Swal.fire({
        title: "Error",
        text: error?.response?.data?.message || "Failed to generate PDF",
        icon: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (pdfFileLink) {
      Swal.fire({
        title: "PDF Generated Successfully",
        text: "Click below to download the PDF",
        icon: "success",
        showConfirmButton: true,
        confirmButtonText: "Download PDF",
      }).then(() => {
        window.open(pdfFileLink, "_blank");
      });
    }
  }, [pdfFileLink]);

  return (
    <div className="max-w-8xl p-6">
      <h2 className="text-3xl font-semibold text-center mb-5 text-gray-800">
        Generate Quotation
      </h2>

      <div className="grid grid-cols-1 md:grid-cols-1 max-w-2xl">
        <div className="bg-white p-6 rounded-lg shadow-lg">
          <h3 className="font-semibold text-xl mb-4 text-gray-700">Bill To</h3>
          <div className="space-y-4">
            <label className="block">
              <span className="text-gray-700">Date</span>
              <input
                type="date"
                className="w-full p-4 border border-gray-300 rounded-lg shadow-sm"
                value={quotationData.billTo?.date ?? currentDate}
                onChange={(e) =>
                  handleChange("billTo", "date", e.target.value)
                }
              />
            </label>
            <label className="block">
              <span className="text-gray-700">Name</span>
              <input
                type="text"
                placeholder="Name"
                className="w-full p-4 border border-gray-300 rounded-lg shadow-sm"
                value={quotationData.billTo.billToName}
                onChange={(e) =>
                  handleChange("billTo", "billToName", e.target.value)
                }
              />
            </label>
            <label className="block">
              <span className="text-gray-700">Phone</span>
              <input
                type="text"
                placeholder="e.g. 03001234567 or +92 300 1234567"
                className={`w-full p-4 border rounded-lg shadow-sm ${phoneError ? "border-red-500" : "border-gray-300"}`}
                value={quotationData.billTo.billToPhone}
                onChange={(e) =>
                  handleChange("billTo", "billToPhone", e.target.value)
                }
              />
              {phoneError && (
                <p className="text-red-500 text-sm mt-1">{phoneError}</p>
              )}
            </label>
            <label className="block">
              <span className="text-gray-700">Address</span>
              <input
                type="text"
                placeholder="Address"
                className="w-full p-4 border border-gray-300 rounded-lg shadow-sm"
                value={quotationData.billTo.billToAddress}
                onChange={(e) =>
                  handleChange("billTo", "billToAddress", e.target.value)
                }
              />
            </label>
          </div>
        </div>
      </div>

      <div className="bg-white p-6 mt-8 rounded-lg shadow-lg flex flex-col gap-3">
        <h3 className="font-semibold text-xl text-gray-700">Products</h3>
        {quotationData.products.map((product, index) => (
          <div key={index} className="flex items-center gap-3">
            <label className="w-full">
              <span className="text-gray-700">Product</span>
              <select
                className="w-full p-3 border rounded-lg"
                value={product.productId}
                onChange={(e) =>
                  handleChange("products", "productId", e.target.value, index)
                }
              >
                <option value="">Select Product</option>
                {productList.map((prod) => (
                  <option key={prod.id} value={prod.id}>
                    {prod.name}
                  </option>
                ))}
              </select>
            </label>
            <label className="w-full">
              <span className="text-gray-700">Variant</span>
              <select
                className="w-full p-3 border rounded-lg"
                value={product.variantId || ""}
                onChange={(e) =>
                  handleChange("products", "variantId", e.target.value, index)
                }
                disabled={!product.productId || !(product.variants?.length > 0)}
              >
                <option value="">Select Variant</option>
                {(product.variants || []).map((variant) => (
                  <option key={variant.id} value={variant.id}>
                    {variant.name ?? "N/A"}
                  </option>
                ))}
              </select>
            </label>
            <label className="w-full">
              <span className="text-gray-700">Variant Color</span>
              <select
                className="w-full p-3 border rounded-lg"
                value={product.variantColorId ?? ""}
                onChange={(e) =>
                  handleChange("products", "variantColorId", e.target.value, index)
                }
                disabled={!product.variantId || !(product.colors?.length > 0)}
              >
                <option value="">Select Color</option>
                {(product.colors || []).map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.color ?? c.textureCode ?? "N/A"}
                  </option>
                ))}
              </select>
            </label>
            <label className="w-full">
              <span className="text-gray-700">Weight</span>
              <input
                type="text"
                placeholder="Weight"
                readOnly
                className="w-full p-3 border rounded-lg bg-gray-100 cursor-not-allowed"
                value={product.weight ?? ""}
              />
            </label>
            <label className="w-full">
              <span className="text-gray-700">Quantity</span>
              <input
                type="number"
                placeholder="Quantity"
                className="w-full p-3 border rounded-lg"
                value={product.quantity}
                onChange={(e) =>
                  handleChange("products", "quantity", e.target.value, index)
                }
              />
            </label>
            <button
              onClick={() => handleRemoveProduct(index)}
              className="text-red-500"
            >
              Remove
            </button>
          </div>
        ))}
        <button
          onClick={handleAddProduct}
          className="btn btn-primary justify-center"
        >
          + Add New Product
        </button>
      </div>

      <div className="flex justify-end gap-3 mt-8">
        <button
          onClick={() => {
          const date = new Date().toISOString().split("T")[0];
          setPhoneError("");
          setQuotationData({
            quotation: { invoiceNo: generateInvoiceNo(), date },
            billTo: { billToName: "", billToPhone: "", billToAddress: "", date },
            products: [{ productId: "", variantId: "", variantColorId: "", variants: [], colors: [], quantity: "", weight: "" }],
          });
        }}
          className="btn btn-reset-filter justify-center"
        >
          Clear
        </button>
        {canGenerate && (
          <button
            onClick={handleSubmit}
            className={`btn btn-primary justify-center ${isLoading ? "opacity-50 cursor-not-allowed" : ""
              }`}
            disabled={isLoading}
          >
            {isLoading ? "Generating PDF..." : "Submit Quotation"}
          </button>
        )}
      </div>
    </div>
  );
}

export default QuotationPage;
